import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, Grid, Card, CardContent, MenuItem } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { CarDto, CarDtoTransmissionEnum, CarDtoStatusEnum, FeaturesBodyTypeEnum, FeaturesFuelTypeEnum, CarImageDto } from "../../apiClient";
import { CarService } from "../../api/services/car.service";

import carDataRaw from "./makes_models.json";

interface CarModelMap {
  [modelName: string]: string;
}

interface CarMakeData {
  id: string;
  models: CarModelMap;
}

interface CarDataMap {
  [makeName: string]: CarMakeData;
}

// Cast to the interface so TS knows the structure
const carData = carDataRaw as CarDataMap;

const AddCar = () => {
  const navigate = useNavigate();
  const { carId } = useParams<{ carId: string }>();


  const [car, setCar] = useState<CarDto>({
    id: "",
    make: "",
    model: "",
    image: "",
    vin: "",
    discountAmount: 0,
    buyingPrice: 0,
    vatRate: 21,
    vatRegime: "FULL",
    status: CarDtoStatusEnum.Available,
    features: {
      bodyType: FeaturesBodyTypeEnum.Sedan,
      mileage: 0,
      fuelType: FeaturesFuelTypeEnum.Petrol,
      power: 0,
      year: 0,
      engineSize: 0,
    },
    price: 0,
    description: "",
    transmission: CarDtoTransmissionEnum.Manual,
  });
  const [images, setImages] = useState<CarImageDto[]>([]);
  const [priceWithVatInput, setPriceWithVatInput] = useState<number>(0);

  useEffect(() => {
    if (carId) {
      CarService.getById(carId).then((carData) => {
        setCar(carData);
        // Calculate price with VAT for the input
        const finalPrice = (carData.price || 0) - (carData.discountAmount || 0);
        const vatRate = carData.vatRate || 21;
        let vatAmount = 0;
        if (carData.vatRegime === 'MARGIN' && carData.buyingPrice) {
          const margin = Math.max(0, finalPrice - carData.buyingPrice);
          vatAmount = margin * (vatRate / 100);
        } else {
          vatAmount = finalPrice * (vatRate / 100);
        }
        setPriceWithVatInput(finalPrice + vatAmount);
        // Load images
        CarService.getImages(carId).then(setImages).catch(console.error);
      });
    }
  }, [carId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === "make") {
        setCar(prev => ({
            ...prev,
            make: value as string,
            model: "" // Reset model when make changes
        }));
        return;
    }

    if (car.features && name in car.features) {
        // Handle nested features
        const numericValue = ['mileage', 'power', 'year', 'engineSize'].includes(name) ? Number(value) : value;
        setCar(prev => ({ ...prev, features: { ...prev.features, [name]: numericValue } }));
    } else {
      const numericValue = ['price', 'discountAmount', 'buyingPrice', 'vatRate'].includes(name) ? Number(value) : value;
      setCar(prev => {
        const updated = { ...prev, [name]: numericValue };

        // Recalculate priceWithVat when price, discountAmount, buyingPrice, vatRate, or vatRegime changes
        if (['price', 'discountAmount', 'buyingPrice', 'vatRate', 'vatRegime'].includes(name)) {
          const finalPrice = (name === 'price' ? Number(value) : updated.price || 0) - (name === 'discountAmount' ? Number(value) : updated.discountAmount || 0);
          const vatRate = name === 'vatRate' ? Number(value) : updated.vatRate || 21;
          const buyingPrice = name === 'buyingPrice' ? Number(value) : updated.buyingPrice || 0;
          const vatRegime = name === 'vatRegime' ? value : updated.vatRegime || 'FULL';

          let vatAmount = 0;
          if (vatRegime === 'MARGIN' && buyingPrice > 0) {
            const margin = Math.max(0, finalPrice - buyingPrice);
            vatAmount = margin * (vatRate / 100);
          } else {
            vatAmount = finalPrice * (vatRate / 100);
          }
          setPriceWithVatInput(finalPrice + vatAmount);
        }

        return updated;
      });
    }
  };

  // Handler for price with VAT input - calculates backwards to set price without VAT
  const handlePriceWithVatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const priceWithVatValue = Number(e.target.value);
    setPriceWithVatInput(priceWithVatValue);

    const vatRate = car.vatRate || 21;

    // Calculate price without VAT based on regime
    if (car.vatRegime === 'MARGIN' && car.buyingPrice) {
      // For MARGIN: priceWithVat = finalPrice + (finalPrice - buyingPrice) * (vatRate / 100)
      // Solving: priceWithVat = finalPrice + finalPrice * (vatRate / 100) - buyingPrice * (vatRate / 100)
      // priceWithVat = finalPrice * (1 + vatRate / 100) - buyingPrice * (vatRate / 100)
      // finalPrice = (priceWithVat + buyingPrice * (vatRate / 100)) / (1 + vatRate / 100)
      const finalPrice = (priceWithVatValue + car.buyingPrice * (vatRate / 100)) / (1 + vatRate / 100);
      setCar(prev => ({ ...prev, price: finalPrice + (prev.discountAmount || 0) }));
    } else {
      // For FULL: priceWithVat = finalPrice * (1 + vatRate / 100)
      // finalPrice = priceWithVat / (1 + vatRate / 100)
      const finalPrice = priceWithVatValue / (1 + vatRate / 100);
      setCar(prev => ({ ...prev, price: finalPrice + (prev.discountAmount || 0) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (carId) {
      CarService.update(carId, car).then(() => {
        navigate('/admin');
      });
    } else {
      CarService.create(car).then((newId) => {
        // Navigate to edit page of the new car so they can add images
        navigate(`/admin/edit-car/${newId}`); // Assuming route structure, or just back to admin
      });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && carId) {
      try {
        await CarService.uploadImage(carId, e.target.files[0], false);
        const updatedImages = await CarService.getImages(carId);
        setImages(updatedImages);
      } catch (error) {
        console.error("Upload failed", error);
        alert("Failed to upload image");
      }
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    if(window.confirm("Are you sure you want to delete this image?")) {
        try {
            await CarService.deleteImage(imageId);
            setImages(images.filter(img => img.id !== imageId));
        } catch (error) {
            console.error("Delete failed", error);
        }
    }
  }

  const handleSetPrimary = async (imageId: string) => {
    if (!carId) return;
    try {
        await CarService.setPrimaryImage(imageId);
        const updatedImages = await CarService.getImages(carId);
        setImages(updatedImages);
    } catch (error) {
        console.error("Set primary failed", error);
    }
  }

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>
        {carId ? "Editează Mașina" : "Adaugă Mașină Nouă"}
      </Typography>
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                 <TextField
                  select
                  fullWidth
                  name="make"
                  value={car.make}
                  onChange={handleChange}
                  label="Marcă"
                  required
                >
                  <MenuItem value=""><em>Selectează Marca</em></MenuItem>
                  
                  {/* Popular Makes */}
                  {["Audi", "BMW", "Mercedes-Benz", "Volkswagen"].map(make => (
                    <MenuItem key={make} value={make} sx={{ fontWeight: 'bold' }}>{make}</MenuItem>
                  ))}
                  
                  <MenuItem disabled>──────────</MenuItem>

                  {/* All Other Makes */}
                  {Object.keys(carData)
                    .filter(make => !["Audi", "BMW", "Mercedes-Benz", "Volkswagen"].includes(make))
                    .sort()
                    .map(make => (
                      <MenuItem key={make} value={make}>{make}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                 <TextField
                  select
                  fullWidth
                  name="model"
                  value={car.model}
                  onChange={handleChange}
                  label="Model"
                  required
                  disabled={!car.make}
                >
                  <MenuItem value=""><em>Selectează Modelul</em></MenuItem>
                  {car.make && carData[car.make] && Object.keys(carData[car.make].models).sort().map(model => (
                    <MenuItem key={model} value={model}>{model}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="transmission"
                  value={car.transmission}
                  onChange={handleChange}
                  label="Transmisie"
                  required
                >
                  <MenuItem value={CarDtoTransmissionEnum.Automatic}>Automată</MenuItem>
                  <MenuItem value={CarDtoTransmissionEnum.Manual}>Manuală</MenuItem>
                </TextField>
              </Grid>
               <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="status"
                  value={car.status}
                  onChange={handleChange}
                  label="Status"
                  required
                >
                  <MenuItem value={CarDtoStatusEnum.Available}>Disponibil</MenuItem>
                  <MenuItem value={CarDtoStatusEnum.Reserved}>Rezervat</MenuItem>
                  <MenuItem value={CarDtoStatusEnum.Sold}>Vândut</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField label="VIN" name="vin" fullWidth value={car.vin} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="bodyType"
                  value={car.features?.bodyType}
                  onChange={handleChange}
                  label="Tip Caroserie"
                  required
                >
                  <MenuItem value={FeaturesBodyTypeEnum.Sedan}>Sedan</MenuItem>
                  <MenuItem value={FeaturesBodyTypeEnum.Suv}>SUV</MenuItem>
                  <MenuItem value={FeaturesBodyTypeEnum.Hatchback}>Hatchback</MenuItem>
                  <MenuItem value={FeaturesBodyTypeEnum.Coupe}>Coupe</MenuItem>
                  <MenuItem value={FeaturesBodyTypeEnum.Convertible}>Convertible</MenuItem>
                  <MenuItem value={FeaturesBodyTypeEnum.Estate}>Estate</MenuItem>
                  <MenuItem value={FeaturesBodyTypeEnum.Mpv}>MPV</MenuItem>
                  <MenuItem value={FeaturesBodyTypeEnum.Pickup}>Pickup</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Kilometraj" name="mileage" type="number" fullWidth value={car.features?.mileage} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="fuelType"
                  value={car.features?.fuelType}
                  onChange={handleChange}
                  label="Combustibil"
                  required
                >
                  <MenuItem value={FeaturesFuelTypeEnum.Petrol}>Benzină</MenuItem>
                  <MenuItem value={FeaturesFuelTypeEnum.Diesel}>Diesel</MenuItem>
                  <MenuItem value={FeaturesFuelTypeEnum.Electric}>Electric</MenuItem>
                  <MenuItem value={FeaturesFuelTypeEnum.Hybrid}>Hibrid</MenuItem>
                  <MenuItem value={FeaturesFuelTypeEnum.PlugInHybrid}>Hibrid Plug-in</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Putere (CP)" name="power" type="number" fullWidth value={car.features?.power} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="An" name="year" type="number" fullWidth value={car.features?.year} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Capacitate Cilindrică (cc)" name="engineSize" type="number" fullWidth value={car.features?.engineSize} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Preț (€)" name="price" type="number" fullWidth value={car.price} onChange={handleChange} required />
              </Grid>
               <Grid item xs={12} sm={6}>
                <TextField label="Discount (€)" name="discountAmount" type="number" fullWidth value={car.discountAmount} onChange={handleChange} />
              </Grid>

              {/* VAT Configuration Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                  Configurare TVA (Admin)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Preț de Achiziție (€)"
                  name="buyingPrice"
                  type="number"
                  fullWidth
                  value={car.buyingPrice || ''}
                  onChange={handleChange}
                  helperText="Referință internă - nu este afișat clienților"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cotă TVA (%)"
                  name="vatRate"
                  type="number"
                  fullWidth
                  value={car.vatRate || 21}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  name="vatRegime"
                  value={car.vatRegime || 'FULL'}
                  onChange={handleChange}
                  label="Regim TVA"
                  helperText="FULL: TVA pe preț întreg | MARGIN: TVA doar pe marjă"
                >
                  <MenuItem value="FULL">TVA pe Preț Întreg</MenuItem>
                  <MenuItem value="MARGIN">TVA pe Marjă (Mașini Second Hand)</MenuItem>
                </TextField>
              </Grid>

              {/* Bidirectional Price Input */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="SAU Preț Final cu TVA (€)"
                  type="number"
                  fullWidth
                  value={priceWithVatInput || ''}
                  onChange={handlePriceWithVatChange}
                  helperText="Modifică prețul cu TVA - se va calcula automat prețul fără TVA"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(33, 150, 243, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      }
                    }
                  }}
                />
              </Grid>

              {/* Profit and Calculated Values Display */}
              {car.price && car.price > 0 && (
                <Grid item xs={12}>
                  <Box sx={{
                    mt: 1,
                    p: 3,
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    borderRadius: 2,
                    border: '2px solid rgba(255, 193, 7, 0.3)'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#FFC107' }}>
                      📊 Calcule Financiare Automate
                    </Typography>

                    <Grid container spacing={2}>
                      {/* Profit Calculation */}
                      {car.buyingPrice && car.buyingPrice > 0 && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{
                            p: 2,
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            borderRadius: 1,
                            border: '1px solid rgba(76, 175, 80, 0.3)'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4CAF50', mb: 1 }}>
                              💰 PROFIT
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#4CAF50' }}>
                              €{Math.max(0, (car.price || 0) - (car.discountAmount || 0) - (car.buyingPrice || 0)).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Preț Vânzare - Preț Achiziție
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {/* Price Breakdown */}
                      <Grid item xs={12} md={car.buyingPrice && car.buyingPrice > 0 ? 6 : 12}>
                        <Box sx={{
                          p: 2,
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          borderRadius: 1,
                          border: '1px solid rgba(33, 150, 243, 0.3)'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2196F3', mb: 1 }}>
                            💵 DEFALCARE PREȚ
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Preț fără TVA:</strong> €{((car.price || 0) - (car.discountAmount || 0)).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>TVA ({car.vatRate}%):</strong> €{(() => {
                              const finalPrice = (car.price || 0) - (car.discountAmount || 0);
                              const vatRate = car.vatRate || 21;
                              if (car.vatRegime === 'MARGIN' && car.buyingPrice && car.buyingPrice > 0) {
                                const margin = Math.max(0, finalPrice - car.buyingPrice);
                                return (margin * (vatRate / 100)).toFixed(2);
                              }
                              return (finalPrice * (vatRate / 100)).toFixed(2);
                            })()}
                            {car.vatRegime === 'MARGIN' && <span style={{ color: '#FF9800', fontSize: '11px' }}> (pe marjă)</span>}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '16px', color: '#FFC107' }}>
                            <strong>TOTAL cu TVA:</strong> €{priceWithVatInput.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField label="Descriere" name="description" fullWidth multiline rows={4} value={car.description} onChange={handleChange} required />
              </Grid>
            </Grid>
            <Box sx={{ marginTop: 2 }}>
              <Button variant="contained" color="primary" type="submit">
                {carId ? "Salvează modificările" : "Adaugă Mașină"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Image Management Section */}
      <Card sx={{ marginTop: 4 }}>
        <CardContent>
            <Typography variant="h5" gutterBottom>
                Imagini Mașină
            </Typography>
            
            {!carId ? (
                <Box sx={{ padding: 3, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography color="textSecondary" variant="subtitle1">
                        Salvați mașina pentru a putea încărca imagini.
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ marginBottom: 2 }}>
                        <Button
                            variant="contained"
                            component="label"
                        >
                            Încarcă Imagine
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageUpload}
                            />
                        </Button>
                    </Box>
                    <Grid container spacing={2}>
                        {images.map((img) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={img.id}>
                                <Card variant="outlined">
                                    <Box sx={{ position: 'relative', height: 200, backgroundColor: '#eee' }}>
                                        <img 
                                            src={(() => {
                                                if (img.id) {
                                                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
                                                    return `${apiUrl}/api/v1/public/car/image/${img.id}`;
                                                }
                                                return img.url || "assets/img/cars/car-01.jpg";
                                            })()}
                                            alt={img.fileName} 
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                                        />
                                        {img.isPrimary && (
                                            <Box sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'primary.main', color: 'white', px: 1, borderRadius: 1, fontSize: '0.8rem' }}>
                                                Primary
                                            </Box>
                                        )}
                                    </Box>
                                    <CardContent>
                                        <Typography variant="caption" display="block" noWrap>
                                            {img.fileName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
                                            {!img.isPrimary && (
                                                <Button size="small" onClick={() => handleSetPrimary(img.id!)}>
                                                    Make Primary
                                                </Button>
                                            )}
                                            <Button size="small" color="error" onClick={() => handleDeleteImage(img.id!)}>
                                                Delete
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {images.length === 0 && (
                            <Grid item xs={12}>
                                <Typography color="textSecondary">Nu există imagini încărcate.</Typography>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        </CardContent>
      </Card>

    </Box>
  );
};

export default AddCar;
